if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', class SlideshowComponent extends SliderComponent {
    constructor() {
      super();
      this.itemSelector = this.dataset.itemSelector || '.slideshow__slide';
      this.ignoreResizeEvent = true;
      this._richSlideIndex = 0;
    }

    // Safety net: force currentPage/active-slide to resync with the actual
    // scroll position shortly after a slide transition is triggered. The
    // native 'scroll' event debounce (see critical-global.js handleScroll)
    // can occasionally miss firing update() after a scrollTo() call — most
    // visibly during autoplay on wide viewports — leaving the "active"
    // slide's heading/badge/button stuck invisible while a different slide
    // is actually on screen. This re-syncs regardless of whether the normal
    // scroll-driven path completed; calling update() again is harmless.
    _forceSyncSoon() {
      clearTimeout(this._forceSyncTimeout);
      this._forceSyncTimeout = setTimeout(() => {
        this.update();
        // setSlideVisibility() drives aria-hidden (and the CSS that keys
        // off it to show/hide each slide's text) but is only ever bound to
        // the native 'scroll' event — it does not get called from update().
        // Re-run it here too, so aria-hidden/text visibility can't end up
        // stuck out of sync with which slide is actually active.
        if (typeof this.setSlideVisibility === 'function') this.setSlideVisibility();
      }, 650);
    }

    init() {
      super.init();
      this.sliderControlWrapper = this.querySelector('.slider-buttons');
      this.enableSliderLooping = true;
      this.autoplayButtonQuerySelector = '.slideshow__autoplay';
      this.autoPlayPausedClass = 'slideshow__autoplay--paused';

      if (!this.sliderControlWrapper) return;

      if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

      this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
      this._prepareIncomingVideoOnScroll = () => {
        if (this._isSlideTransitioning) return;

        const incomingPage = this._getPageFromScroll();
        if (incomingPage !== this.currentPage) {
          this.prepareIncomingVideo(incomingPage - 1);
        }
      };
      this.slider.addEventListener('scroll', this._prepareIncomingVideoOnScroll, { passive: true });
      this._onVisibilityChange = this.handleVisibilityChange.bind(this);
      document.addEventListener('visibilitychange', this._onVisibilityChange);
      this.setSlideVisibility();

      this.querySelectorAll('.banner__price').forEach(ele => {
        ele.innerHTML = Shopify.showPrice(ele.dataset.price * 1);
      });

      // This component sets ignoreResizeEvent = true in its constructor, which
      // stops the base SliderComponent's own 'resize' listener from ever being
      // attached (see critical-global.js: the listener is only added when
      // !ignoreResizeEvent). That means initPages() — which recalculates
      // sliderItemOffset/totalPages/maxScrollLeft from the live DOM — never
      // reruns after the window's width actually changes, e.g. when a browser
      // window is dragged from a laptop display to an external monitor in
      // extended-desktop mode. The slider then keeps scrolling/measuring
      // against stale pre-resize offsets, which desyncs currentPage from the
      // real layout and can leave every slide's text permanently hidden until
      // the page is reloaded. Add our own debounced resize handler so a width
      // change always triggers a fresh measurement plus a full visibility
      // resync, regardless of ignoreResizeEvent.
      this._onWindowResize = () => {
        clearTimeout(this._resizeSyncTimeout);
        this._resizeSyncTimeout = setTimeout(() => {
          this.initPages();
          this.update();
          if (typeof this.setSlideVisibility === 'function') this.setSlideVisibility();
        }, 300);
      };
      window.addEventListener('resize', this._onWindowResize);
    }

    disconnectedCallback() {
      if (this.slider && this._prepareIncomingVideoOnScroll) {
        this.slider.removeEventListener('scroll', this._prepareIncomingVideoOnScroll);
      }
      if (this._onVisibilityChange) {
        document.removeEventListener('visibilitychange', this._onVisibilityChange);
      }
      if (this._onWindowResize) {
        window.removeEventListener('resize', this._onWindowResize);
        clearTimeout(this._resizeSyncTimeout);
      }

      super.disconnectedCallback();
    }

    _getPageFromScroll() {
      if (this._forcedRichPage) return this._forcedRichPage;
      return super._getPageFromScroll();
    }

    update() {
      if (this._isSlideTransitioning) return;

      super.update();

      // Update active slide classes
      const oldActive = this.querySelector(`${this.itemSelector}.active`);
      if (oldActive) oldActive.classList.remove('active');

      const previousActiveSlide = this._activeSlide;
      const activeSlide = this.getActiveSlide();
      if (activeSlide) activeSlide.classList.add('active');
      const activeSlideChanged = previousActiveSlide && activeSlide && activeSlide !== previousActiveSlide;
      this._activeSlide = activeSlide;

      if (this.slider.classList.contains('rich-slideshow')) {
        this._richSlideIndex = this.currentPage - 1;
      }

      if (!activeSlideChanged && this._incomingVideoSlide !== activeSlide) {
        const incomingMedia = this._incomingVideoSlide?.querySelector('deferred-media');
        if (incomingMedia) this.pauseDeferredMediaPlayback(incomingMedia, true);
        this._incomingVideoSlide = null;
      }

      if (activeSlideChanged && (!this.isAutoplay || !this.autoplayButtonIsSetToPlay)) {
        this.playVideo(true);
      }

      // --- Animate autoplay button (visual sync) ---
      if (this.sliderAutoplayButton) {
        this.sliderAutoplayButton.classList.remove(this.autoPlayPausedClass);
        this.sliderAutoplayButton
          .querySelector('.slideshow__progress__path')
          .getAnimations()
          .forEach(anim => {
            anim.cancel();
            anim.play();
          });
      }
    }

    getActiveSlide() {
      // Defensive guard: ensure currentPage is valid
      if (!this.currentPage || isNaN(this.currentPage)) {
        // Try to reset pages if not initialized yet
        if (typeof this.initPages === 'function') {
          this.initPages();
        }

        // Fallback to first visible slide if still invalid
        this.currentPage = this.currentPage && !isNaN(this.currentPage)
          ? this.currentPage
          : 1;
      }

      // Ensure itemSelector is valid
      const selector = `${this.itemSelector}:nth-child(${this.currentPage})`;

      try {
        return this.querySelector(selector);
      } catch (err) {
        console.warn('⚠️ [SlideshowComponent] Invalid selector:', selector, err);
        // fallback gracefully
        return this.querySelector(this.itemSelector);
      }
    }

    setSlideVisibility() {
      const activeElement = document.activeElement;
      const activeIndex = this.currentPage ? this.currentPage - 1 : 0;

      this.sliderItemsToShow.forEach((item, index) => {
        const linkElements = item.querySelectorAll('a');
        const isActive = index === activeIndex;

        // If a focused element is inside a slide that is about to be hidden,
        // move focus somewhere safe before toggling aria-hidden.
        if (!isActive && activeElement && item.contains(activeElement)) {
          const activeSlide = this.sliderItemsToShow[activeIndex];

          // Prefer a focusable element inside the active slide
          let focusTarget =
            activeSlide &&
            activeSlide.querySelector(
              'a[href], button, [role="button"], [tabindex]:not([tabindex="-1"])'
            );

          // Fallback: use an active dot or navigation button
          if (!focusTarget) {
            focusTarget =
              this.querySelector('.slider-counter__link[aria-current="true"]') ||
              this.querySelector('.slider-button--next, .slider-button--prev') ||
              this; // as a last resort, focus slider container
          }

          if (focusTarget && typeof focusTarget.focus === 'function') {
            focusTarget.focus();
          } else if (typeof activeElement.blur === 'function') {
            activeElement.blur();
          }
        }

        if (isActive) {
          if (linkElements.length) {
            linkElements.forEach((button) => {
              button.removeAttribute('tabindex');
            });
          }
          item.setAttribute('aria-hidden', 'false');
          item.removeAttribute('tabindex');
        } else {
          if (linkElements.length) {
            linkElements.forEach((button) => {
              button.setAttribute('tabindex', '-1');
            });
          }
          item.setAttribute('aria-hidden', 'true');
          item.setAttribute('tabindex', '-1');
        }
      });
    }

    play() {
      super.play();
      this.playVideo();
      this.runAutoplayButton();
    }

    resetDeferredMediaPlayback(dm) {
      const media = dm?.deferredElement || dm?.querySelector('video, iframe');

      if (!media) return;

      if (media.nodeName === 'VIDEO') {
        try {
          media.currentTime = 0;
        } catch (e) {
          // Ignore videos that cannot seek yet.
        }
        return;
      }

      if (media.classList.contains('js-youtube')) {
        media.contentWindow?.postMessage('{"event":"command","func":"seekTo","args":[0,true]}', '*');
        return;
      }

      if (media.classList.contains('js-vimeo')) {
        media.contentWindow?.postMessage('{"method":"setCurrentTime","value":0}', '*');
      }
    }

    pauseDeferredMediaPlayback(dm, reset = false) {
      if (!dm?.deferredElement) return;

      window.pauseAllMedia(dm, true, false);
      dm.isPlaying = false;
      dm.playPromise = undefined;
      dm.classList.remove('playing');

      if (reset) {
        this.resetDeferredMediaPlayback(dm);
        this._rewoundMedia ??= new WeakSet();
        this._rewoundMedia.add(dm);
      }
    }

    cleanupVideoListeners() {
      clearTimeout(this._incomingVideoPrepareTimeout);
      this._incomingVideoPrepareTimeout = null;

      if (this._videoListeners?.length) {
        this._videoListeners.forEach(({ el, type, handler }) => {
          el.removeEventListener(type, handler);
        });
      }
      this._videoListeners = [];
    }

    resetAutoplayProgress() {
      const progressPath = this.sliderAutoplayButton?.querySelector('.slideshow__progress__path');
      if (!progressPath) return;

      progressPath.getAnimations().forEach(animation => animation.cancel());
      progressPath.style.animation = 'none';
      progressPath.style.strokeDashoffset = '1';
    }

    syncAutoplayProgressWithVideo(video, play = !video.paused && !video.ended) {
      if (!this.sliderAutoplayButton || !video || video.nodeName !== 'VIDEO') return;

      const currentTime = Number.isFinite(video.currentTime) ? video.currentTime : 0;
      const duration = Number.isFinite(video.duration) ? video.duration : 0;
      const progressTime = Math.min(currentTime, duration || currentTime) * 1000;
      const progressPath = this.sliderAutoplayButton.querySelector('.slideshow__progress__path');

      progressPath?.getAnimations().forEach(animation => {
        animation.currentTime = progressTime;
        if (play && this.isAutoplay && this.autoplayButtonIsSetToPlay) {
          animation.play();
        } else {
          animation.pause();
        }
      });
    }

    handleVisibilityChange() {
      const activeVideo = this.getActiveSlide()?.querySelector('deferred-media')?.deferredElement;
      if (!activeVideo || activeVideo.nodeName !== 'VIDEO') return;

      this.syncAutoplayProgressWithVideo(activeVideo, !document.hidden && !activeVideo.paused && !activeVideo.ended);
    }

    prepareIncomingVideo(index, preserveCurrentPlayback = false) {
      const incomingSlide = this.sliderItemsToShow?.[index];
      if (!incomingSlide || incomingSlide === this._activeSlide || incomingSlide === this._incomingVideoSlide) return;

      this._incomingVideoSlide = incomingSlide;
      if (!preserveCurrentPlayback) {
        this.cleanupVideoListeners();
        if (this.isAutoplay && this.autoplayButtonIsSetToPlay) {
          clearTimeout(this.autoplay);
        }
      }

      const dm = incomingSlide.querySelector('deferred-media');
      if (!dm || !window.customElements?.get('deferred-media')) return;

      try {
        customElements.upgrade(dm);

        const activeMedia = this._activeSlide?.querySelector('deferred-media');
        this.querySelectorAll('deferred-media').forEach(media => {
          if (media !== dm && media !== activeMedia) {
            this.pauseDeferredMediaPlayback(media, true);
          }
        });

        const videoWasRewound = this._rewoundMedia?.has(dm);
        if (!videoWasRewound) this.resetDeferredMediaPlayback(dm);

        this._rewoundMedia?.delete(dm);
        dm.isPlaying = false;
        dm.playPromise = undefined;
        dm.classList.remove('playing');
        dm.play();

        this._lastVideoSlide = incomingSlide;
      } catch (e) {
        // Ignore DOM upgrade race conditions.
      }
    }

    transitionRichSlideshow(index) {
      if (this._isSlideTransitioning) return;

      const firstSlide = this.sliderItemsToShow?.[0];
      const targetSlide = this.sliderItemsToShow?.[index];
      if (!firstSlide || !targetSlide) return;

      const targetScrollLeft = targetSlide.offsetLeft - firstSlide.offsetLeft;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.slider.scrollTo({ left: targetScrollLeft });
        this._richSlideIndex = index;
        return;
      }

      this._isSlideTransitioning = true;
      this.slider.classList.add('slider--no-snap');
      this.animateScrollTo(targetScrollLeft, 600, 'easeOutCubic', () => {
        this.slider.scrollLeft = targetScrollLeft;
        this._richSlideIndex = index;

        requestAnimationFrame(() => {
          this._isSlideTransitioning = false;
          this.slider.classList.remove('slider--no-snap');
          this._forcedRichPage = index + 1;
          this.update();
          this._forcedRichPage = null;
        });
      });
    }

    goToSlideByIndex(index) {
      if (this.slider.classList.contains('rich-slideshow')) {
        if (this._isSlideTransitioning) return;
        this.prepareIncomingVideo(index);
        this.transitionRichSlideshow(index);
      } else {
        this.prepareIncomingVideo(index);
        super.goToSlideByIndex(index);
        this._forceSyncSoon();
      }
    }

    autoRotateSlides() {
      const targetIndex = this.slider.classList.contains('rich-slideshow')
        ? (this._richSlideIndex + 1) % this.sliderItemsToShow.length
        : (this.currentPage === this.totalPages ? 0 : this.currentPage);
      if (this.slider.classList.contains('rich-slideshow')) {
        if (this._isSlideTransitioning) return;
        this.prepareIncomingVideo(targetIndex);
        this.beforeUpdateAutoplay();
        this.transitionRichSlideshow(targetIndex);
      } else {
        this.prepareIncomingVideo(targetIndex);
        super.autoRotateSlides();
        this._forceSyncSoon();
      }
    }

    onButtonClick(event) {
      const step = parseInt(event.currentTarget.dataset.step || '1', 10) || 1;
      const currentIndex = this.currentPage - 1;
      const direction = event.currentTarget.name === 'next' ? 1 : -1;
      const maxIndex = this.sliderItemsToShow.length - 1;
      let targetIndex = currentIndex + direction * step;

      if (this.enableSliderLooping) {
        if (targetIndex < 0) targetIndex = maxIndex;
        if (targetIndex > maxIndex) targetIndex = 0;
      } else {
        targetIndex = Math.max(0, Math.min(targetIndex, maxIndex));
      }

      if (this.slider.classList.contains('rich-slideshow')) {
        event.preventDefault();
        if (this._isSlideTransitioning) return;
        this.prepareIncomingVideo(targetIndex);
        this.transitionRichSlideshow(targetIndex);
      } else {
        this.prepareIncomingVideo(targetIndex);
        super.onButtonClick(event);
        this._forceSyncSoon();
      }
    }

    onDragMove(event) {
      super.onDragMove(event);
      if (!this.isDragging || !this.sliderItemOffset) return;

      const pointerDelta = event.pageX - this.dragStartX;
      if (Math.abs(pointerDelta) <= Math.abs(this.sliderItemOffset) * 0.2) return;

      const dragToLeft = pointerDelta < 0;
      const direction = document.dir !== 'rtl'
        ? (dragToLeft ? 1 : -1)
        : (dragToLeft ? -1 : 1);
      const maxIndex = this.sliderItemsToShow.length - 1;
      const targetIndex = Math.max(0, Math.min(this.currentPage - 1 + direction, maxIndex));

      this.prepareIncomingVideo(targetIndex);
    }

    playVideo(reset = false) {
      // --- Cleanup old video event listeners ---
      this.cleanupVideoListeners();

      const activeSlide = this.getActiveSlide();
      if (this._incomingVideoSlide === activeSlide) this._incomingVideoSlide = null;
      // --- Handle DeferredMedia playback ---
      const dm = activeSlide ? activeSlide.querySelector('deferred-media') : null;

      this.querySelectorAll('deferred-media').forEach(media => {
        if (media === dm) return;

        // Pause all other DeferredMedia when slide changes
        if (window.customElements?.get('deferred-media')) {
          try {
            customElements.upgrade(media);
            this.pauseDeferredMediaPlayback(media, true);
          } catch (e) {
            // Ignore DOM upgrade race conditions
          }
        }
      });

      if (this.prevButton) this.prevButton.removeAttribute('disabled');

      // Helper: update CSS --speed for autoplay button
      const setAutoplaySpeedCSS = (seconds) => {
        if (this.sliderAutoplayButton) {
          this.sliderAutoplayButton.style.setProperty('--speed', `${seconds}s`);
        }
      };

      if (!dm) {
        this._lastVideoSlide = activeSlide;

        // No video → use default autoplay delay
        setAutoplaySpeedCSS(this.autoplaySpeed / 1000);

        if (this.isAutoplay && this.autoplayButtonIsSetToPlay) {
          clearTimeout(this.autoplay);
          this.autoplay = setTimeout(() => this.autoRotateSlides(), this.autoplaySpeed);
        }
        return;
      }

      if (this.isAutoplay && this.autoplayButtonIsSetToPlay) {
        clearTimeout(this.autoplay);
      }

      // --- Video slide handling ---
      const shouldResetVideo = reset || activeSlide !== this._lastVideoSlide;
      if (shouldResetVideo) {
        const videoWasRewound = this._rewoundMedia?.has(dm);
        if (!videoWasRewound) this.resetDeferredMediaPlayback(dm);

        this._rewoundMedia?.delete(dm);
        dm.isPlaying = false;
        dm.playPromise = undefined;
        dm.classList.remove('playing');
      }

      dm.play().then(() => {
        const getVideoEl = () => dm.deferredElement || dm.querySelector('video');

        const startWithDuration = () => {
          const videoEl = getVideoEl();
          if (!videoEl) return;

          const duration = Number.isFinite(videoEl.duration) && videoEl.duration > 0
            ? videoEl.duration
            : this.autoplaySpeed / 1000;
          const isNativeVideo = videoEl.nodeName === 'VIDEO';
          const elapsed = isNativeVideo && Number.isFinite(videoEl.currentTime)
            ? videoEl.currentTime
            : 0;
          const remainingDuration = Math.max(duration - elapsed, 0.1);
          setAutoplaySpeedCSS(isNativeVideo ? duration : remainingDuration);

          // Autoplay slideshow delay based on video duration
          if (this.isAutoplay && this.autoplayButtonIsSetToPlay) {
            clearTimeout(this.autoplay);
            this.runAutoplayButton();

            if (isNativeVideo) {
              const onPlay = () => this.syncAutoplayProgressWithVideo(videoEl, true);
              const onPause = () => this.syncAutoplayProgressWithVideo(videoEl, false);
              videoEl.addEventListener('play', onPlay);
              videoEl.addEventListener('pause', onPause);
              this._videoListeners.push(
                { el: videoEl, type: 'play', handler: onPlay },
                { el: videoEl, type: 'pause', handler: onPause }
              );
              this.syncAutoplayProgressWithVideo(videoEl);

              const activeIndex = this.sliderItemsToShow.indexOf(activeSlide);
              const isLastRichSlide = this.slider.classList.contains('rich-slideshow')
                && activeIndex === this.sliderItemsToShow.length - 1;

              if (isLastRichSlide && this.sliderItemsToShow[0]?.querySelector('deferred-media')) {
                const prepareDelay = Math.max((remainingDuration - 0.3) * 1000, 0);
                this._incomingVideoPrepareTimeout = setTimeout(() => {
                  this.prepareIncomingVideo(0, true);
                }, prepareDelay);
              }

              const onEnded = () => {
                clearTimeout(this.autoplay);
                this.resetAutoplayProgress();
                this.autoRotateSlides();
              };
              videoEl.addEventListener('ended', onEnded, { once: true });
              this._videoListeners.push({ el: videoEl, type: 'ended', handler: onEnded });
            } else {
              this.autoplay = setTimeout(
                () => this.autoRotateSlides(),
                remainingDuration * 1000
              );
            }
          }
        };

        const waitForVideo = () => {
          const videoEl = getVideoEl();
          if (!videoEl) {
            requestAnimationFrame(waitForVideo);
            return;
          }
          if (isNaN(videoEl.duration) || !videoEl.duration) {
            const onLoaded = startWithDuration;
            videoEl.addEventListener('loadedmetadata', onLoaded, { once: true });
            this._videoListeners.push({ el: videoEl, type: 'loadedmetadata', handler: onLoaded });
          } else {
            startWithDuration();
          }
        };

        waitForVideo();
      });

      this._lastVideoSlide = activeSlide;
    }

    pause() {
      super.pause();
      this.runAutoplayButton(false);
      const activeSlide = this.getActiveSlide();
      const dm = activeSlide ? activeSlide.querySelector('deferred-media') : null;
      if(dm) {
        dm.pause();
      }
    }

    runAutoplayButton(play = true) {
      if(this.sliderAutoplayButton) {
        this.sliderAutoplayButton.classList.toggle(this.autoPlayPausedClass, !play);
        const progressPath = this.sliderAutoplayButton.querySelector('.slideshow__progress__path');
        if (play) {
          progressPath.style.removeProperty('animation');
          progressPath.style.removeProperty('stroke-dashoffset');
        }
        progressPath.getAnimations().forEach((anim) => {
          if(play) {
            anim.cancel();
            anim.play();
          } else {
            anim.pause();
          }
        });
      }
    }

    beforeUpdateAutoplay() {
      if(this.sliderAutoplayButton) {
        this.sliderAutoplayButton.querySelector('.slideshow__progress__path').getAnimations().forEach((anim) => {
          anim.pause();
        });
      }
    }
  });
}
